import React, { createContext, useContext, ReactNode } from "react";
import {
  AnalyticsEvents,
  AnalyticsProps,
  trackAnalyticsEvent,
  identifyUser,
  resetUser,
  isFeatureEnabled,
  getFeatureFlag,
} from "../../utils/analytics";

// Interface for the Analytics Context
interface AnalyticsContextType {
  // Core tracking methods
  trackEvent: typeof trackAnalyticsEvent;
  trackLogin: (method: string, user: any) => void;
  trackLoginAttempt: (method: string) => void;
  trackLoginFailure: (method: string, errorMessage: string, errorReason: string) => void;
  trackPageView: (path: string, props?: Record<string, any>) => void;
  trackBookingEvent: (eventName: string, bookingData: any) => void;
  trackSearchEvent: (searchParams: any) => void;
  trackListingView: (listingData: any) => void;

  // Feature-specific tracking methods
  trackInvoiceDownload: (invoiceId: string, userId: number) => void;
  trackStatementDownload: (startDate: string, endDate: string, userId: number) => void;
  trackSettingsNavigation: (section: string, userRole: string) => void;
  trackProfileUpdate: (fields: string[], userId: number) => void;
  trackPropertyImageUpload: (propertyId: number, imageCount: number, success: boolean) => void;

  // PostHog Feature Flags & User Identification
  identifyUser: typeof identifyUser;
  resetUser: typeof resetUser;
  isFeatureEnabled: typeof isFeatureEnabled;
  getFeatureFlag: typeof getFeatureFlag;
}

// Create the Analytics Context
const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

// Props for the Analytics Provider
interface AnalyticsProviderProps {
  children: ReactNode;
}

// Create the Analytics Provider component
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  // Pageviews are captured automatically by the Plausible tracker on load and
  // on SPA route changes (via the History API). No manual pageview calls
  // needed here - avoid firing them twice.

  // Track login attempt
  const trackLoginAttempt = (method: string) => {
    trackAnalyticsEvent(AnalyticsEvents.LOGIN_ATTEMPTED, {
      [AnalyticsProps.LOGIN_METHOD]: method,
    });
  };

  // Track successful login
  const trackLogin = (method: string, user: any) => {
    trackAnalyticsEvent(AnalyticsEvents.USER_LOGIN, {
      [AnalyticsProps.USER_ROLE]: user.role,
      [AnalyticsProps.LOGIN_METHOD]: method,
    });
  };

  // Track login failure
  const trackLoginFailure = (method: string, errorMessage: string, errorReason: string) => {
    trackAnalyticsEvent(AnalyticsEvents.USER_LOGIN_FAILED, {
      [AnalyticsProps.ERROR_MESSAGE]: errorMessage,
      [AnalyticsProps.ERROR_REASON]: errorReason,
      [AnalyticsProps.LOGIN_METHOD]: method,
    });
  };

  // Track page view
  const trackPageView = (path: string, props: Record<string, any> = {}) => {
    trackAnalyticsEvent("$pageview", { url: path, ...props });
  };

  // Track booking event
  const trackBookingEvent = (eventName: string, bookingData: any) => {
    trackAnalyticsEvent(eventName, {
      [AnalyticsProps.BOOKING_ID]: bookingData.bookingId,
      [AnalyticsProps.LISTING_ID]: bookingData.listingId,
      [AnalyticsProps.START_DATE]: bookingData.startDate,
      [AnalyticsProps.END_DATE]: bookingData.endDate,
      [AnalyticsProps.TOTAL_DAYS]: bookingData.totalDays,
      [AnalyticsProps.TOTAL_AMOUNT]: bookingData.totalAmount,
      [AnalyticsProps.BOOKING_FOR_OTHERS]: bookingData.bookingForOthers || false,
      // Add other booking-specific properties as needed
    });
  };

  // Track search event
  const trackSearchEvent = (searchParams: any) => {
    trackAnalyticsEvent(AnalyticsEvents.SEARCH_PERFORMED, {
      [AnalyticsProps.LOCATION]: searchParams.location,
      [AnalyticsProps.DATE_RANGE]: searchParams.dateRange,
      [AnalyticsProps.NUM_FILTERS]: searchParams.filters?.length || 0,
      // Add other search-specific properties as needed
    });
  };

  // Track listing view
  const trackListingView = (listingData: any) => {
    trackAnalyticsEvent(AnalyticsEvents.LISTING_VIEWED, {
      [AnalyticsProps.LISTING_ID]: listingData.id,
      [AnalyticsProps.PROPERTY_TYPE]: listingData.propertyType,
      [AnalyticsProps.LISTING_NAME]: listingData.name,
      [AnalyticsProps.VIEW_SOURCE]: listingData.viewSource || "direct",
      [AnalyticsProps.PRICE_PER_DAY]: listingData.pricePerDay,
      // Add other listing-specific properties as needed
    });
  };

  // Web-specific tracking methods

  // Track invoice download
  const trackInvoiceDownload = (invoiceId: string, userId: number) => {
    trackAnalyticsEvent(AnalyticsEvents.INVOICE_DOWNLOADED, {
      [AnalyticsProps.INVOICE_ID]: invoiceId,
      user_id: userId,
    });
  };

  // Track statement download
  const trackStatementDownload = (startDate: string, endDate: string, userId: number) => {
    trackAnalyticsEvent(AnalyticsEvents.STATEMENT_DOWNLOADED, {
      [AnalyticsProps.START_DATE]: startDate,
      [AnalyticsProps.END_DATE]: endDate,
      [AnalyticsProps.STATEMENT_PERIOD]: `${startDate} to ${endDate}`,
      user_id: userId,
    });
  };

  // Track settings navigation
  const trackSettingsNavigation = (section: string, userRole: string) => {
    trackAnalyticsEvent(AnalyticsEvents.SETTINGS_SECTION_VIEWED, {
      [AnalyticsProps.SETTINGS_SECTION]: section,
      [AnalyticsProps.USER_ROLE]: userRole,
      [AnalyticsProps.PAGE_NAME]: `settings_${section}`,
    });
  };

  // Track profile update
  const trackProfileUpdate = (fields: string[], userId: number) => {
    trackAnalyticsEvent(AnalyticsEvents.PROFILE_UPDATED, {
      [AnalyticsProps.PROFILE_FIELD_CHANGED]: fields,
      field_count: fields.length,
      user_id: userId,
    });
  };

  // Track property image upload
  const trackPropertyImageUpload = (propertyId: number, imageCount: number, success: boolean) => {
    if (success) {
      trackAnalyticsEvent(AnalyticsEvents.PROPERTY_IMAGE_UPLOADED, {
        [AnalyticsProps.PROPERTY_ID]: propertyId,
        image_count: imageCount,
      });
    } else {
      trackAnalyticsEvent(AnalyticsEvents.PROPERTY_IMAGE_UPLOAD_FAILED, {
        [AnalyticsProps.PROPERTY_ID]: propertyId,
        image_count: imageCount,
      });
    }
  };

  // Value object for the Analytics Context
  const value: AnalyticsContextType = {
    trackEvent: trackAnalyticsEvent,
    trackLogin,
    trackLoginAttempt,
    trackLoginFailure,
    trackPageView,
    trackBookingEvent,
    trackSearchEvent,
    trackListingView,

    // Web-specific methods
    trackInvoiceDownload,
    trackStatementDownload,
    trackSettingsNavigation,
    trackProfileUpdate,
    trackPropertyImageUpload,

    // PostHog Feature Flags & User Identification
    identifyUser,
    resetUser,
    isFeatureEnabled,
    getFeatureFlag,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

// Custom hook to use Analytics
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

// Analytics utility functions for direct import
export const Analytics = {
  // Original events
  trackEvent: trackAnalyticsEvent,
  Events: AnalyticsEvents,
  Props: AnalyticsProps,

  // Web events
  trackWebEvent: trackAnalyticsEvent,
  WebEvents: AnalyticsEvents,
  WebProps: AnalyticsProps,
};

export default Analytics;
